/**
 * Chat message components for RevisionChatDialog.
 * Clean, log-style layout — no bubbles, no avatars, no gradients.
 */
import React from 'react';
import { Box, Typography, Chip, CircularProgress, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EVENT_CONFIG from './chatEventConfig';
import { formatEventDetails } from './chatEventUtils';
import { formatParsedResult, parsedForSample } from '../../utils/formatParsed';

/* ── User message ──────────────────────────────────────────────────── */

export function UserMessage({ text }) {
  return (
    <Box sx={{ py: 1.25, px: 2, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
      <Typography sx={{ color: '#475569', fontSize: '0.7rem', fontWeight: 500, mb: 0.5 }}>
        You
      </Typography>
      <Typography sx={{ color: '#e2e8f0', fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    </Box>
  );
}

/* ── Agent event row ─────────────────────────────────────────────── */

export function AgentEventBubble({ event }) {
  const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.tool_call;
  const { title, details } = formatEventDetails(event);

  // 'done' is a sentinel; 'complete' is rendered by CompletionBanner
  if (event.type === 'done' || event.type === 'complete') return null;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 1.5,
      py: 0.6, px: 2,
      '&:hover': { background: 'rgba(255,255,255,0.01)' },
    }}>
      <Typography sx={{
        color: config.color,
        fontSize: '0.7rem',
        fontWeight: 500,
        minWidth: 80,
        flexShrink: 0,
        fontFamily: 'monospace',
        opacity: 0.8,
      }}>
        {config.label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ color: '#cbd5e1', fontSize: '0.82rem', lineHeight: 1.5 }}>
          {title}
        </Typography>
        {details && (
          <Typography sx={{
            color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.15,
          }}>
            {details}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ── Thinking indicator ────────────────────────────────────────────── */

export function AgentThinkingIndicator() {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.75, px: 2,
    }}>
      <CircularProgress size={12} thickness={5} sx={{ color: '#475569' }} />
      <Typography sx={{ color: '#475569', fontSize: '0.8rem' }}>
        Processing...
      </Typography>
    </Box>
  );
}

/* ── Completion banner with detailed summary ──────────────────────── */

const SAMPLE_STATUS_STYLE = {
  fixed:          { color: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.18)', icon: CheckCircleIcon,  label: 'Fixed' },
  unfixed:        { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.18)',  icon: ErrorIcon,        label: 'Unfixed' },
  false_positive: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.18)', icon: HelpOutlineIcon,  label: 'False Positive' },
  passing:        { color: '#64748b', bg: 'rgba(255,255,255,0.02)', border: 'rgba(255,255,255,0.06)', icon: CheckCircleIcon,  label: 'Passing' },
  regression:     { color: '#f97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.18)', icon: ReportProblemIcon, label: 'Regression' },
};

function MiniSampleRow({ r, parsed }) {
  const cfg = SAMPLE_STATUS_STYLE[r.status] || SAMPLE_STATUS_STYLE.unfixed;
  const Icon = cfg.icon;
  const parsedLine = formatParsedResult(parsed);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, px: 1.25, borderRadius: 0.75, background: cfg.bg, border: `1px solid ${cfg.border}`, mb: 0.5 }}>
      <Icon sx={{ color: cfg.color, fontSize: 14, mt: 0.2, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ color: cfg.color, fontSize: '0.68rem', fontWeight: 600 }}>{cfg.label}</Typography>
          <Typography sx={{ color: '#475569', fontSize: '0.65rem', fontFamily: 'monospace' }}>#{r.sample_index}</Typography>
        </Box>
        {(r.expected_value || r.extracted_value || parsedLine) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, mt: 0.15, minWidth: 0 }}>
            {r.expected_value != null && (
              <Typography sx={{ color: '#64748b', fontSize: '0.66rem', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                <span style={{ color: '#475569' }}>exp:</span>{' '}
                {typeof r.expected_value === 'string' ? r.expected_value : JSON.stringify(r.expected_value)}
              </Typography>
            )}
            {r.extracted_value != null && (
              <Typography sx={{ color: '#64748b', fontSize: '0.66rem', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                <span style={{ color: '#475569' }}>got:</span>{' '}
                {typeof r.extracted_value === 'string' ? r.extracted_value : JSON.stringify(r.extracted_value)}
              </Typography>
            )}
            {parsedLine && (
              <Typography sx={{ color: parsedLine.ok ? '#64748b' : '#ef4444', fontSize: '0.66rem', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                <span style={{ color: '#475569' }}>parsed:</span> {parsedLine.text}
              </Typography>
            )}
          </Box>
        )}
        {r.reason && <Typography sx={{ color: '#64748b', fontSize: '0.66rem', fontStyle: 'italic', mt: 0.15 }}>{r.reason}</Typography>}
      </Box>
    </Box>
  );
}

export function CompletionBanner({ reviewContext }) {
  if (!reviewContext) return null;

  const verdict = reviewContext.repair_verdict;
  const isVerified = verdict === 'VERIFIED';
  const isPartial = verdict === 'PARTIALLY_VERIFIED';
  const results = reviewContext.per_sample_results || [];
  const parsedCheck = reviewContext.parsed_config_check;

  const counts = { fixed: 0, unfixed: 0, false_positive: 0, passing: 0, regression: 0 };
  results.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });
  const failingSamples = results.filter((r) => !r.is_passing_sample);
  const fixedPct = failingSamples.length > 0
    ? Math.round(((counts.fixed + counts.false_positive) / failingSamples.length) * 100)
    : 0;

  const borderColor = isVerified ? 'rgba(16,185,129,0.15)' : isPartial ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)';
  const bgColor = isVerified ? 'rgba(16,185,129,0.04)' : isPartial ? 'rgba(245,158,11,0.04)' : 'rgba(239,68,68,0.04)';
  const headColor = isVerified ? '#6ee7b7' : isPartial ? '#fbbf24' : '#fca5a5';
  const headText = isVerified
    ? 'Revision complete — config verified ✅'
    : isPartial
    ? 'Revision complete — partial fix ⚠️'
    : 'Revision complete — needs review';

  return (
    <Box sx={{ mx: 2, mt: 1.5, mb: 0.5, px: 2, py: 1.5, borderRadius: 1, border: `1px solid ${borderColor}`, background: bgColor }}>
      {/* Header */}
      <Typography sx={{ color: headColor, fontSize: '0.84rem', fontWeight: 600 }}>{headText}</Typography>

      {/* Summary chips */}
      {results.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
          {counts.fixed > 0 && <Chip label={`${counts.fixed} fixed`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500, color: '#10b981', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)' }} />}
          {counts.unfixed > 0 && <Chip label={`${counts.unfixed} unfixed`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500, color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }} />}
          {counts.false_positive > 0 && <Chip label={`${counts.false_positive} false positive`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500, color: '#f59e0b', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }} />}
          {counts.regression > 0 && <Chip label={`${counts.regression} regression`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500, color: '#f97316', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.15)' }} />}
        </Box>
      )}

      {/* Progress bar */}
      {failingSamples.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <LinearProgress variant="determinate" value={fixedPct}
            sx={{ flex: 1, height: 5, borderRadius: 2, background: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': { borderRadius: 2, background: fixedPct === 100 ? '#10b981' : fixedPct >= 50 ? '#f59e0b' : '#ef4444' } }} />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 500, minWidth: 32, textAlign: 'right' }}>{fixedPct}%</Typography>
        </Box>
      )}

      {/* Agent summary */}
      {reviewContext.agent_summary && (
        <Box sx={{ mt: 1, px: 1.25, py: 1, borderRadius: 0.75, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography sx={{ color: '#475569', fontSize: '0.66rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
            Agent Summary
          </Typography>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.78rem', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
            {reviewContext.agent_summary.length > 600
              ? reviewContext.agent_summary.slice(0, 600) + '…'
              : reviewContext.agent_summary}
          </Typography>
        </Box>
      )}

      {/* Per-sample details (failing only — concise for chat) */}
      {results.filter((r) => !r.is_passing_sample).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: '#475569', fontSize: '0.7rem', fontWeight: 500, mb: 0.5 }}>
            Sample Details
          </Typography>
          {results.filter((r) => !r.is_passing_sample).map((r, i) => (
            <MiniSampleRow key={i} r={r} parsed={parsedForSample(parsedCheck, r)} />
          ))}
        </Box>
      )}

      {/* Regression tests */}
      {counts.regression > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ color: '#475569', fontSize: '0.68rem', fontWeight: 500, mb: 0.25 }}>Regression Tests</Typography>
          {results.filter((r) => r.is_passing_sample && r.status === 'regression').map((r, i) => (
            <MiniSampleRow key={`reg-${i}`} r={r} parsed={parsedForSample(parsedCheck, r)} />
          ))}
        </Box>
      )}

      {/* Hint */}
      <Typography sx={{ color: '#475569', fontSize: '0.72rem', mt: 1, borderTop: '1px solid rgba(255,255,255,0.04)', pt: 0.75 }}>
        {isPartial
          ? 'Some samples remain unfixed. Send feedback to help the agent, or close to review the config diff.'
          : 'Send more feedback or close to review the updated config.'}
      </Typography>
    </Box>
  );
}
